import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProductSettings } from '@workspace/utils-react';
import type { Settings } from '../../AppContainer';
import { getProjectMetadata, upsertProjectMetadata, getAuditLogs } from '../../api/project-metadata';
import type { UpsertProjectMetadataRequest } from '../../api/project-metadata';

export const projectMetadataQueryKey = ['project-metadata'] as const;

export function useProjectMetadata() {
  const { apiUrl } = useProductSettings<Settings>();
  return useQuery({
    queryKey: projectMetadataQueryKey,
    queryFn: () => getProjectMetadata(apiUrl),
    staleTime: 30 * 1000,
  });
}

export function useUpsertProjectMetadata() {
  const { apiUrl } = useProductSettings<Settings>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ areaLevel1, body }: { areaLevel1: string; body: UpsertProjectMetadataRequest }) =>
      upsertProjectMetadata(apiUrl, areaLevel1, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectMetadataQueryKey });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useAuditLogs(recordId: string | null) {
  const { apiUrl } = useProductSettings<Settings>();
  return useQuery({
    queryKey: ['audit-logs', 'ProjectMetadata', recordId] as const,
    queryFn: () => getAuditLogs(apiUrl, recordId!),
    enabled: recordId !== null,
    staleTime: 10 * 1000,
  });
}
